import { resolveSubstrateBackData } from '@/utils/substrates.js'

/** Convert slot widths (base + reveal per slot) to the renderer's stacked increments. */
export function buildMatWidths(matSlots, matWidth) {
  return matSlots.map((slot, index) => {
    const reveal = (slot - (matSlots[index - 1] || 1)) * 0.25
    return Object.fromEntries(Object.entries(matWidth).map(([side, width]) =>
      [side, (index === 0 ? width : 0) + reveal]
    ))
  })
}

/**
 * Compute a FrameRenderer-compatible frameConfig from a flat array of chosen options.
 *
 * Works with two callers:
 *   - design-studio.vue: passes rehydrated item.config_data.chosen_options (has extra_data)
 *   - DesignStudioItemForm: normalizes effectiveOptions + availableOptions to the same shape
 *
 * @param {string} substrate - Substrate name (e.g. 'Print', 'Canvas')
 * @param {Array<{name: string, value: *, option_id: number, extra_data: object|null}>} chosenOptions
 */
export function buildFrameConfigFromChosenOptions(substrate, chosenOptions) {
  const opts = Array.isArray(chosenOptions) ? chosenOptions : []
  const findOpt = (pattern) => opts.find(o => pattern.test(o.name))

  // An item can carry both; the frame is the one legacy measures — frame, else
  // float frame (project/models.py: `if frame ... elif float_frame`). Only
  // usable when extra_data has top_image (same check DesignStudioItemForm uses).
  const frameOpt = findOpt(/^frame$/i) ?? findOpt(/^float frame$/i)
  const frameData = frameOpt?.extra_data?.top_image ? frameOpt.extra_data : null

  const floatFrameData = findOpt(/^float frame$/i)?.extra_data ?? null

  const cmOpt = findOpt(/canvas\s*mount/i)
  const canvasMount = cmOpt ? (cmOpt.value != null ? String(cmOpt.value) : cmOpt.name || '') : null

  const stOpt = findOpt(/stretcher/i)
  const stretcherDepth = stOpt != null
    ? (() => {
        const p = parseFloat(String(stOpt.value ?? stOpt.name ?? '').replace(/["']/g, ''))
        return isNaN(p) ? null : p
      })()
    : null

  // Mat data — Mat 1, Mat 2, Mat 3 ordered
  const matSlots = []
  const matDataArray = [1, 2, 3].flatMap(n => {
    const opt = opts.find(o => new RegExp(`^Mat\\s*${n}$`, 'i').test(o.name))
    if (!opt?.extra_data?.color) return []
    matSlots.push(n)
    return [{ color: opt.extra_data.color, depth: 0.0625, coreColor: opt.extra_data.color }]
  })
  const matData = matDataArray[0] ?? null

  // Mat widths — single uniform option or per-side (Top/Bottom/Left/Right)
  const matWidthsArray = (() => {
    if (!matDataArray.length) return []
    const total = { top: 2, bottom: 2, left: 2, right: 2 }
    opts.forEach(o => {
      if (!/mat\s*width/i.test(o.name)) return
      const val = parseFloat(o.value)
      if (isNaN(val)) return
      const lower = o.name.toLowerCase()
      if (lower.includes('top'))         total.top = val
      else if (lower.includes('bottom')) total.bottom = val
      else if (lower.includes('left'))   total.left = val
      else if (lower.includes('right'))  total.right = val
      else { total.top = total.bottom = total.left = total.right = val }
    })
    return buildMatWidths(matSlots, total)
  })()

  const matWidth = matWidthsArray.length === 0
    ? { top: 0, bottom: 0, left: 0, right: 0 }
    : matWidthsArray.length === 1
      ? matWidthsArray[0]
      : matWidthsArray.reduce(
          (a, w) => ({ top: a.top + w.top, bottom: a.bottom + w.bottom, left: a.left + w.left, right: a.right + w.right }),
          { top: 0, bottom: 0, left: 0, right: 0 }
        )

  // Linen liner — a numeric option ("Linen Liner", 1" or 2"), Canvas-only.
  // Its value is the width per side, so it enlarges the finished piece.
  const linenLinerOpt = findOpt(/linen\s*liner/i)
  const linenLiner = linenLinerOpt
    ? (parseFloat(String(linenLinerOpt.value ?? linenLinerOpt.name ?? '').replace(/["']/g, '')) || 0)
    : 0

  const hangOpt = findOpt(/hang/i)
  const hangData = hangOpt
    ? { name: hangOpt.value != null ? String(hangOpt.value) : hangOpt.name || '' }
    : null

  const artworkFilter = (() => {
    let grayscale = false
    let sepia = false
    opts.forEach(o => {
      const cat = String(o.name || '').trim().toLowerCase()
      const val = String(o.value ?? o.name ?? '').trim().toLowerCase()
      const on = ['yes', 'true', '1', 'on'].includes(val)
      if (/black\s*(and|&)\s*white/.test(cat)) grayscale = on
      if (/sepia/.test(cat)) sepia = on
    })
    return (grayscale || sepia) ? { grayscale, sepia } : null
  })()

  const substrateBackData = resolveSubstrateBackData(substrate, (pattern) => {
    const opt = findOpt(pattern)
    return opt ? (opt.value != null ? String(opt.value) : opt.name || '') : null
  })

  const acousticTwoSidedChangeable = opts.some(o => {
    const raw = String(o.value != null ? o.value : o.name || '').toLowerCase()
    const cat = String(o.name || '').toLowerCase()
    return /2\s*[-\s]*sided\s*changeable/i.test(raw) || /2\s*[-\s]*sided\s*changeable/i.test(cat)
  })

  return {
    substrate,
    frameData,
    matData,
    matDataArray,
    matSlots,
    matWidth,
    matWidthsArray,
    hangData,
    stretcherDepth,
    canvasMount,
    floatFrameData,
    substrateBackData,
    artworkFilter,
    acousticTwoSidedChangeable,
    linenLiner,
  }
}
